import React from 'react';
import { AlertTriangle, BarChart3, Check, CheckCircle, Download, FileText, Key, Loader2, Mail, RefreshCw, Save, Send, Target, Users, X, XCircle, Zap } from 'lucide-react';

const ReportsTab = ({ ctx }) => {
  const {  summary, members, reports, parentReportHistory, analytics, qbrData, digestSettings, alerts, API_URL, getToken, previewing, setPreviewing, setDigestSettings , ccInput, digestResult, generatingQbr, parentReportResult, savingDigest, sendingDigest, sendingParentReports, setCcInput, setDigestResult, setGeneratingQbr, setParentReportResult, setSavingDigest, setSendingDigest, setSendingParentReports, setQbrData } = ctx;

  return (
<div data-testid="reports-tab">
  <h1 className="text-xl font-bold mb-1" style={{ color: '#1A1209' }}>Monthly Reports</h1>
  <p className="text-xs mb-5" style={{ color: '#999' }}>
    Aggregated monthly reports for your organisation. Individual team member data is never included.
  </p>

  {/* Preview button — visible when organisation has at least 1 student */}
  {(summary?.total || 0) > 0 && (
    <div className="bg-white rounded-xl p-4 mb-5 flex items-center justify-between" style={{ border: '1px solid #E8E8E8' }} data-testid="preview-report-section">
      <div>
        <p className="text-sm font-semibold" style={{ color: '#1A1209' }}>Preview This Month&apos;s Report</p>
        <p className="text-[10px]" style={{ color: '#999' }}>Generates a watermarked PDF using real data from this month to date.</p>
      </div>
      <button onClick={async () => {
        setPreviewing(true);
        try {
          const token = getToken();
          const res = await fetch(`${API_URL}/api/reports/preview/corporate`, {
            method: 'POST', headers: { Authorization: `Bearer ${token}` },
          });
          if (!res.ok) { const d = await res.json().catch(() => ({})); alert(d.detail || 'Preview generation failed'); return; }
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          const cd = res.headers.get('content-disposition') || '';
          const fnMatch = cd.match(/filename="?([^"]+)"?/);
          const fn = fnMatch ? fnMatch[1] : `HiBFF_Preview_Report.pdf`;
          const a = document.createElement('a'); a.href = url; a.download = fn; a.click();
          URL.revokeObjectURL(url);
        } catch { alert('Failed to generate preview'); }
        setPreviewing(false);
      }}
      disabled={previewing}
      className="px-4 py-2 rounded-lg text-xs font-semibold text-white flex items-center gap-1.5 disabled:opacity-50"
      style={{ backgroundColor: '#7B4FD8' }}
      data-testid="preview-report-btn">
        {previewing ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
        {previewing ? 'Generating...' : "Preview This Month's Report"}
      </button>
    </div>
  )}

  {reports.length === 0 ? (
    <div className="bg-white rounded-xl p-8 text-center" style={{ border: '1px solid #E8E8E8' }}>
      <FileText size={32} className="mx-auto mb-3" style={{ color: '#DDD' }} />
      <p className="text-sm mb-1" style={{ color: '#999' }}>No reports generated yet</p>
      <p className="text-xs" style={{ color: '#CCC' }}>Reports are auto-generated on the 1st of each month after your organisation has been active for at least 14 days.</p>
    </div>
  ) : (
    <div className="space-y-3">
      {reports.map(r => (
        <div key={r.report_id} className="bg-white rounded-xl p-5 flex items-center justify-between" style={{ border: '1px solid #E8E8E8' }} data-testid={`report-${r.report_id}`}>
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#F5831F15' }}>
              <FileText size={18} style={{ color: '#F5831F' }} />
            </div>
            <div>
              <p className="text-sm font-bold" style={{ color: '#1A1209' }}>{r.month_name}</p>
              <p className="text-xs" style={{ color: '#999' }}>
                {r.active_students} active students &middot; {r.total_sessions} sessions &middot; {r.challenges_completed} challenges
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[10px]" style={{ color: '#CCC' }}>
              {new Date(r.generated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </span>
            <a href={`${API_URL}/api/reports/corporate/${r.year}/${r.month}/download`}
              onClick={(e) => {
                e.preventDefault();
                const token = getToken();
                fetch(`${API_URL}/api/reports/corporate/${r.year}/${r.month}/download`, { headers: { Authorization: `Bearer ${token}` } })
                  .then(res => res.blob())
                  .then(blob => {
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a'); a.href = url; a.download = r.filename; a.click();
                    URL.revokeObjectURL(url);
                  });
              }}
              className="px-3 py-1.5 rounded-lg text-xs font-medium text-white"
              style={{ backgroundColor: '#F5831F' }}
              data-testid={`download-report-${r.report_id}`}>
              Download PDF
            </a>
          </div>
        </div>
      ))}
    </div>
  )}

  {/* Parent Reports Section */}
  <div className="bg-white rounded-xl p-5 mt-6" style={{ border: '1px solid #E8E8E8' }} data-testid="parent-reports-section">
    <div className="flex items-center justify-between mb-3">
      <div>
        <h3 className="text-sm font-bold flex items-center gap-2" style={{ color: '#1A1209' }}>
          <Mail size={14} style={{ color: '#2563EB' }} /> Parent Progress Reports
        </h3>
        <p className="text-[10px]" style={{ color: '#999' }}>Send engagement summaries to parents for each student.</p>
      </div>
      <button
        onClick={async () => {
          setSendingParentReports(true); setParentReportResult(null);
          try {
            const res = await fetch(`${API_URL}/api/corporate/reports/send-parent`, {
              method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
              body: JSON.stringify({ send_all: true }),
            });
            if (res.ok) setParentReportResult(await res.json());
          } catch {}
          setSendingParentReports(false);
        }}
        disabled={sendingParentReports}
        className="px-4 py-2 rounded-lg text-xs font-semibold text-white flex items-center gap-1.5 disabled:opacity-50"
        style={{ backgroundColor: '#2563EB' }} data-testid="send-all-parent-reports"
      >
        {sendingParentReports ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
        Send to All Parents
      </button>
    </div>
    {parentReportResult && (
      <div className="p-3 rounded-lg mb-3" style={{ backgroundColor: '#ECFDF5' }}>
        <p className="text-xs font-semibold" style={{ color: '#065F46' }}>{parentReportResult.message}</p>
      </div>
    )}
    {parentReportHistory.length > 0 && (
      <div>
        <h4 className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: '#999' }}>Recent Parent Reports</h4>
        <div className="space-y-1.5">
          {parentReportHistory.slice(0, 10).map(r => (
            <div key={r.report_id} className="flex items-center justify-between px-3 py-2 rounded-lg" style={{ backgroundColor: '#F8F7F4' }}>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium" style={{ color: '#1A1209' }}>{r.student_name}</span>
                <span className="text-[10px]" style={{ color: '#999' }}>{r.parent_email}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px]" style={{ color: '#999' }}>{r.period}</span>
                <span className="px-1.5 py-0.5 rounded text-[9px] font-bold" style={{ backgroundColor: '#ECFDF5', color: '#059669' }}>Sent</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>

  {/* ── QBR (Quarterly Business Review) Section ────────── */}
  <div className="bg-white rounded-xl p-5 mt-6" style={{ border: '1px solid #E8E8E8' }} data-testid="qbr-section">
    <div className="flex items-center justify-between mb-4">
      <div>
        <h3 className="text-sm font-bold flex items-center gap-2" style={{ color: '#1A1209' }}>
          <BarChart3 size={14} style={{ color: '#6366F1' }} /> Quarterly Business Review (QBR)
        </h3>
        <p className="text-[10px]" style={{ color: '#999' }}>Auto-generated executive summary for stakeholder presentations.</p>
      </div>
      <button onClick={async () => {
        setGeneratingQbr(true);
        try {
          const res = await fetch(`${API_URL}/api/corporate/qbr-report`, { headers: { Authorization: `Bearer ${getToken()}` } });
          if (res.ok) setQbrData(await res.json());
        } catch {}
        setGeneratingQbr(false);
      }} disabled={generatingQbr}
        className="px-4 py-2 rounded-lg text-xs font-semibold text-white flex items-center gap-1.5 disabled:opacity-50"
        style={{ backgroundColor: '#6366F1' }} data-testid="refresh-qbr-btn">
        {generatingQbr ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
        {generatingQbr ? 'Generating...' : 'Refresh QBR'}
      </button>
    </div>

    {qbrData && (
      <div className="space-y-4" data-testid="qbr-content">
        {qbrData.demo_data && (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-[10px] font-medium" style={{ backgroundColor: '#EFF6FF', color: '#1E40AF' }}>
            <BarChart3 size={10} /> Sample data — will reflect real metrics once team members are active
          </div>
        )}

        <div className="p-4 rounded-xl" style={{ background: 'linear-gradient(135deg, #1A1209, #3D2E1A)' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-widest" style={{ color: '#F5831F' }}>Quarterly Business Review</p>
              <h2 className="text-lg font-bold text-white mt-1">{qbrData.org_name}</h2>
              <p className="text-xs" style={{ color: '#A89880' }}>{qbrData.quarter}</p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-black" style={{ color: '#F5831F' }}>{qbrData.executive_summary?.active_participation_pct}%</p>
              <p className="text-[10px]" style={{ color: '#A89880' }}>Active Participation</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Total Members', value: qbrData.executive_summary?.total_members, color: '#1A1209' },
            { label: 'Total Sessions', value: qbrData.executive_summary?.total_sessions?.toLocaleString(), color: '#3B82F6' },
            { label: 'Sessions / Member', value: qbrData.executive_summary?.sessions_per_member, color: '#059669' },
            { label: 'OCEAN Completion', value: `${qbrData.executive_summary?.ocean_completion_pct}%`, color: '#8B5CF6' },
          ].map(m => (
            <div key={m.label} className="p-3 rounded-lg text-center" style={{ backgroundColor: '#F8F7F4' }}>
              <p className="text-xl font-black" style={{ color: m.color }}>{m.value}</p>
              <p className="text-[10px] font-medium" style={{ color: '#999' }}>{m.label}</p>
            </div>
          ))}
        </div>

        {qbrData.key_highlights?.length > 0 && (
          <div className="p-4 rounded-lg" style={{ backgroundColor: '#F0FDF4', border: '1px solid #D1FAE5' }}>
            <h4 className="text-xs font-bold mb-2" style={{ color: '#065F46' }}>Key Highlights</h4>
            <ul className="space-y-1.5">
              {qbrData.key_highlights.map((h, i) => (
                <li key={i} className="flex items-start gap-2 text-xs" style={{ color: '#047857' }}>
                  <CheckCircle size={12} className="mt-0.5 flex-shrink-0" style={{ color: '#10B981' }} />
                  {h}
                </li>
              ))}
            </ul>
          </div>
        )}

        {qbrData.recommendations?.length > 0 && (
          <div className="p-4 rounded-lg" style={{ backgroundColor: '#FFFBEB', border: '1px solid #FDE68A' }}>
            <h4 className="text-xs font-bold mb-2" style={{ color: '#92400E' }}>Recommendations</h4>
            <ul className="space-y-1.5">
              {qbrData.recommendations.map((r, i) => (
                <li key={i} className="flex items-start gap-2 text-xs" style={{ color: '#B45309' }}>
                  <Target size={12} className="mt-0.5 flex-shrink-0" style={{ color: '#F59E0B' }} />
                  {r}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    )}
  </div>

  {/* ── EMAIL DIGEST CONFIGURATION ─────────────────────── */}
  <div className="bg-white rounded-xl p-5 mt-6" style={{ border: '1px solid #E8E8E8' }} data-testid="email-digest-section">
    <div className="flex items-center justify-between mb-4">
      <div>
        <h3 className="text-sm font-bold flex items-center gap-2" style={{ color: '#1A1209' }}>
          <Mail size={14} style={{ color: '#F5831F' }} /> Executive Email Digest
        </h3>
        <p className="text-[10px]" style={{ color: '#999' }}>Auto-send QBR snapshot, pulse score, alerts, and goals to your inbox.</p>
      </div>
      <button onClick={async () => {
        setSendingDigest(true);
        setDigestResult(null);
        try {
          const res = await fetch(`${API_URL}/api/corporate/email-digest/send-now`, {
            method: 'POST', headers: { Authorization: `Bearer ${getToken()}` },
          });
          const data = await res.json();
          setDigestResult(data);
        } catch { setDigestResult({ error: 'Failed to send' }); }
        setSendingDigest(false);
      }} disabled={sendingDigest}
        className="px-4 py-2 rounded-lg text-xs font-semibold text-white flex items-center gap-1.5 disabled:opacity-50"
        style={{ backgroundColor: '#F5831F' }} data-testid="send-digest-now-btn">
        {sendingDigest ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
        {sendingDigest ? 'Sending...' : 'Send Now'}
      </button>
    </div>

    {digestResult && (
      <div className={`px-4 py-2 rounded-lg text-xs mb-4 ${digestResult.sent ? '' : ''}`} style={{ backgroundColor: digestResult.sent ? '#F0FDF4' : '#FEF2F2', color: digestResult.sent ? '#166534' : '#991B1B', border: `1px solid ${digestResult.sent ? '#BBF7D0' : '#FECACA'}` }} data-testid="digest-result">
        {digestResult.sent ? <span className="flex items-center gap-1.5"><CheckCircle size={12} /> Digest sent to {digestResult.recipients?.join(', ')}</span> : <span className="flex items-center gap-1.5"><AlertTriangle size={12} /> {digestResult.error || 'Failed to send digest'}</span>}
      </div>
    )}

    {digestSettings && (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-[10px] font-semibold uppercase tracking-wider mb-1.5 block" style={{ color: '#999' }}>Frequency</label>
          <select value={digestSettings.frequency} onChange={e => setDigestSettings(p => ({ ...p, frequency: e.target.value }))}
            className="w-full px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="digest-frequency">
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] font-semibold uppercase tracking-wider mb-1.5 block" style={{ color: '#999' }}>CC Recipients</label>
          <div className="flex gap-2">
            <input value={ccInput} onChange={e => setCcInput(e.target.value)} placeholder="email@company.com"
              className="flex-1 px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="digest-cc-input"
              onKeyDown={e => { if (e.key === 'Enter' && ccInput.trim()) { setDigestSettings(p => ({ ...p, cc_emails: [...(p.cc_emails || []), ccInput.trim()] })); setCcInput(''); } }} />
            <button onClick={() => { if (ccInput.trim()) { setDigestSettings(p => ({ ...p, cc_emails: [...(p.cc_emails || []), ccInput.trim()] })); setCcInput(''); } }}
              className="px-3 py-2 rounded-lg text-xs font-medium" style={{ backgroundColor: '#F0F0F0', color: '#666' }} data-testid="add-cc-btn">Add</button>
          </div>
          {digestSettings.cc_emails?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {digestSettings.cc_emails.map((email, i) => (
                <span key={i} className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px]" style={{ backgroundColor: '#F5831F15', color: '#F5831F' }}>
                  {email}
                  <button onClick={() => setDigestSettings(p => ({ ...p, cc_emails: p.cc_emails.filter((_, idx) => idx !== i) }))} className="hover:opacity-70"><X size={10} /></button>
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="sm:col-span-2">
          <label className="text-[10px] font-semibold uppercase tracking-wider mb-2 block" style={{ color: '#999' }}>Include in Digest</label>
          <div className="flex flex-wrap gap-3">
            {[{ key: 'include_qbr', label: 'QBR Summary' }, { key: 'include_pulse', label: 'Pulse Score' }, { key: 'include_alerts', label: 'Insight Alerts' }, { key: 'include_goals', label: 'Goal Progress' }].map(opt => (
              <label key={opt.key} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg cursor-pointer text-xs" style={{
                backgroundColor: digestSettings[opt.key] ? '#F5831F15' : '#F8F7F4',
                color: digestSettings[opt.key] ? '#F5831F' : '#999',
                border: `1px solid ${digestSettings[opt.key] ? '#F5831F33' : '#E8E8E8'}`,
              }}>
                <input type="checkbox" checked={digestSettings[opt.key]} onChange={e => setDigestSettings(p => ({ ...p, [opt.key]: e.target.checked }))} className="hidden" />
                {digestSettings[opt.key] ? <CheckCircle size={12} /> : <XCircle size={12} />}
                {opt.label}
              </label>
            ))}
          </div>
        </div>
        <div className="sm:col-span-2 flex justify-end">
          <button onClick={async () => {
            setSavingDigest(true);
            try {
              await fetch(`${API_URL}/api/corporate/email-digest/settings`, {
                method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
                body: JSON.stringify(digestSettings),
              });
            } catch {}
            setSavingDigest(false);
          }} disabled={savingDigest}
            className="px-4 py-2 rounded-lg text-xs font-semibold text-white flex items-center gap-1.5 disabled:opacity-50"
            style={{ backgroundColor: '#2EAA5C' }} data-testid="save-digest-settings-btn">
            {savingDigest ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
            Save Preferences
          </button>
        </div>
      </div>
    )}

    {digestSettings?.last_sent && (
      <p className="text-[9px] mt-3" style={{ color: '#CCC' }}>
        Last sent: {new Date(digestSettings.last_sent).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
      </p>
    )}
  </div>

  {/* ── EXPORT EVERYTHING ──────────────────────────────── */}
  <div className="bg-white rounded-xl p-5 mt-6" style={{ border: '1px solid #E8E8E8' }} data-testid="export-section">
    <h3 className="text-sm font-bold mb-1 flex items-center gap-2" style={{ color: '#1A1209' }}>
      <Download size={14} style={{ color: '#6366F1' }} /> Export Data
    </h3>
    <p className="text-[10px] mb-4" style={{ color: '#999' }}>Download your data in CSV format for board presentations, HR reports, or compliance audits.</p>
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      {[
        { label: 'Analytics Report', desc: 'Department performance, OCEAN, engagement', endpoint: 'analytics-csv', icon: BarChart3 },
        { label: 'Team Roster', desc: 'Full member list with departments and status', endpoint: 'roster-csv', icon: Users },
        { label: 'Pulse Survey Data', desc: 'Question breakdown, scores, and trends', endpoint: 'pulse-csv', icon: Zap },
      ].map(exp => (
        <button key={exp.endpoint}
          onClick={() => {
            const link = document.createElement('a');
            link.href = `${API_URL}/api/corporate/export/${exp.endpoint}`;
            link.setAttribute('download', '');
            const token = getToken();
            fetch(`${API_URL}/api/corporate/export/${exp.endpoint}`, { headers: { Authorization: `Bearer ${token}` } })
              .then(r => r.blob())
              .then(blob => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `hibff_${exp.endpoint}_${new Date().toISOString().split('T')[0]}.csv`;
                a.click();
                URL.revokeObjectURL(url);
              });
          }}
          className="flex items-start gap-3 p-4 rounded-xl text-left transition-all hover:shadow-sm group"
          style={{ backgroundColor: '#F8F7F4', border: '1px solid #E8E8E8' }}
          data-testid={`export-${exp.endpoint}-btn`}
        >
          <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform" style={{ backgroundColor: '#6366F115' }}>
            <exp.icon size={18} style={{ color: '#6366F1' }} />
          </div>
          <div>
            <p className="text-xs font-bold" style={{ color: '#1A1209' }}>{exp.label}</p>
            <p className="text-[10px]" style={{ color: '#999' }}>{exp.desc}</p>
            <span className="text-[9px] font-semibold flex items-center gap-1 mt-1" style={{ color: '#6366F1' }}>
              <Download size={9} /> Download CSV
            </span>
          </div>
        </button>
      ))}
    </div>
  </div>
</div>
  );
};

export default ReportsTab;
